// Types for the MCP gateway admin views (Organización → Servidores MCP).
// The executor proxies these from the mcp-gateway service.

/** One MCP server known to CubicOS (`mongodb`, `brain`). The list/detail split
 * is built for more (Holded, cubic-api) to land without a UI rewrite. */
export interface McpServerSummary {
  id: string;
  name: string;
  description: string;
}

// Mirrors KNOWN_SERVER_IDS in the executor
// (executor/domains/mcp_gateway/routes/mcp_gateway_routes.py) and KNOWN_SERVERS
// in builder-backend (services/mcp-access.service.ts). Which of them an
// organization actually has turned on is per tenant — see McpTenantAccess.
export const MCP_SERVERS: McpServerSummary[] = [
  {
    id: 'mongodb',
    name: 'MongoDB Atlas',
    description: 'Acceso de solo lectura a leads y reuniones vía Claude, con login por empleado.',
  },
  {
    id: 'brain',
    name: 'Brain',
    description: 'Búsqueda de solo lectura en el conocimiento interno de la organización (procesos, protocolos, estrategia).',
  },
];

export type McpChildStatus = 'starting' | 'ready' | 'down';

export interface McpServerStatus {
  status: string;
  service: string;
  version: string;
  /** Per-backend state; the only source for servers other than Mongo. */
  backends?: Record<string, { status: string; tools: number }>;
  /** Mongo child state — kept for the Mongo server, absent when Mongo is not offered. */
  child?: McpChildStatus;
  tools?: number;
  access?: { configured: boolean; lastFetchOk: boolean; lastFetchAt: string | null; enabledServers: number };
}

export interface McpTool {
  name: string;
  description: string;
}

export interface McpCatalog {
  server: {
    id: string;
    name: string;
    readOnly: boolean;
    version: string;
    upstream: string;
    childStatus: McpChildStatus;
  };
  connection: {
    mcpUrl: string;
    clientId: string;
    authorizationServer: string;
    redirectUris: string[];
    allowedDomain: string;
    identityProvider?: string;
    /** Mongo only. */
    database?: string;
  };
  tools: McpTool[];
}

export interface McpActivityEntry {
  ts: string;
  tenant?: string;
  server?: string;
  email: string;
  tool: string;
  database?: string;
  collection?: string;
  /** Brain search: passages returned (0 = a gap in the knowledge base). */
  resultCount?: number;
  topScore?: number;
  durationMs: number;
  isError: boolean;
}

export interface McpActivityResponse {
  instance: string;
  persistent: boolean;
  entries: McpActivityEntry[];
}

export interface McpCollectionsResponse {
  database: string;
  collections: string[];
}

export interface McpSampleFieldsResponse {
  database: string;
  collection: string;
  fields: string[];
}

// Policy CRUD talks to builder-backend directly (the policy authority),
// not through the executor — see mcp-gateway/README.md "Guardrails".
// Input for a PUT: no `database` per entry — it's one shared field on the
// request body (see mcp-policy.service.ts `savePolicies` for why).
export interface McpCollectionPolicyInput {
  collection: string;
  allowed: boolean;
  hiddenFields: string[];
}

export interface McpCollectionPolicy {
  database: string;
  collection: string;
  allowed: boolean;
  hiddenFields: string[];
}

export interface McpPolicyResponse {
  policies: McpCollectionPolicy[];
  /** Field names hidden on every collection, per database. */
  globalHiddenFields: Record<string, string[]>;
}

// ── Per-organization access ──
// builder-backend is the authority (/agent/builder/mcp-access), like the policy.

export type McpDefaultAccess = 'members' | 'none';
export type McpPrincipalType = 'email' | 'role';

export interface McpAccessRule {
  principalType: McpPrincipalType;
  principal: string;
  allowed: boolean;
}

export interface McpTenantServerAccess {
  tenant: string;
  serverId: string;
  enabled: boolean;
  defaultAccess: McpDefaultAccess;
  rules: McpAccessRule[];
  /** How many members can actually use it right now, after rules and default. */
  grantedCount: number;
}

export interface McpTenantAccess {
  tenant: string;
  memberCount: number;
  servers: McpTenantServerAccess[];
  identityProvider: {
    tenant: string;
    type: string;
    allowedDomains: string[];
    /** A verified person on these domains joins the organization as a viewer on first sign-in. */
    autoProvision: boolean;
  } | null;
}

export interface BrainIndexStatus {
  tenant: string;
  documents: number;
  chunks: number;
  last_indexed_at: string | null;
}

export interface BrainReindexResponse {
  status: string;
  total: number;
  indexed: number;
  /** Documents left alone because the index already held exactly their text. */
  skipped?: number;
  /** Indexed documents that no longer exist and were removed (first page only). */
  orphansRemoved?: number;
  errors: string[];
  done: boolean;
  nextOffset: number;
}
